import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import ProfileImage from '@/client/components/common/ProfileImage';
import {
  AdminMessage,
  ConversationRegistry,
  MessagingPartySummary,
  coerceId,
  inquiryVisitorName,
  isInquiryMessage,
  partyDisplayName,
  partyPhotoUrl,
} from '@/client/types/messaging';
import { cn } from '@/lib/utils';

const HoverShell = ({
  children,
  card,
  className,
}: {
  children: ReactNode;
  card: ReactNode;
  className?: string;
}) => (
  <span className={cn('group/party relative inline-flex max-w-full', className)}>
    {children}
    <span
      className={cn(
        'pointer-events-none invisible absolute left-0 top-full z-50 mt-1 w-64',
        'translate-y-1 opacity-0 transition duration-150',
        'group-hover/party:pointer-events-auto group-hover/party:visible group-hover/party:translate-y-0 group-hover/party:opacity-100',
      )}
    >
      <span className="block rounded-lg border border-gray-200 bg-white p-3 shadow-lg ring-1 ring-black/5">
        {card}
      </span>
    </span>
  </span>
);

const ProfileCardBody = ({
  summary,
  kind,
  id,
}: {
  summary?: MessagingPartySummary | null;
  kind: 'user' | 'org';
  id: number;
}) => {
  const name = partyDisplayName(summary, kind === 'org' ? `Org ${id}` : `User ${id}`);
  const photo = partyPhotoUrl(summary);
  const href = kind === 'org' ? `/admin/users/organization?oid=${id}` : `/admin/users?uid=${id}`;
  const subtitle =
    kind === 'org'
      ? summary?.ffid
        ? `ffid ${summary.ffid}`
        : `oid ${id}`
      : summary?.email || (summary?.username ? `@${summary.username}` : `uid ${id}`);

  return (
    <div className="flex items-start gap-3">
      <ProfileImage name={name} src={photo} size="w-12 h-12" textSize="text-sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-gray-900">{name}</p>
        <p className="truncate text-xs text-gray-500">{subtitle}</p>
        {kind === 'user' && summary?.username && summary?.email ? (
          <p className="mt-0.5 truncate text-[11px] text-gray-400">uid {id}</p>
        ) : null}
        <Link
          to={href}
          onClick={(e) => e.stopPropagation()}
          className="mt-2 inline-flex text-xs font-medium text-blue-700 hover:underline"
        >
          View {kind === 'org' ? 'organization' : 'profile'}
        </Link>
      </div>
    </div>
  );
};

export function UserPartyLink({
  uid,
  summary,
  className,
}: {
  uid?: number | string | null;
  summary?: MessagingPartySummary | null;
  className?: string;
}) {
  const id = coerceId(uid);
  if (id == null) return <span className="text-sm text-gray-400">—</span>;
  const name = partyDisplayName(summary, `User ${id}`);
  return (
    <HoverShell
      className={className}
      card={<ProfileCardBody summary={summary} kind="user" id={id} />}
    >
      <Link
        to={`/admin/users?uid=${id}`}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex max-w-full items-center gap-1.5 truncate text-sm font-medium text-blue-700 hover:underline"
      >
        <ProfileImage name={name} src={partyPhotoUrl(summary)} size="w-5 h-5" textSize="text-[9px]" />
        <span className="truncate">{name}</span>
      </Link>
    </HoverShell>
  );
}

export function OrgPartyLink({
  oid,
  summary,
  className,
}: {
  oid?: number | string | null;
  summary?: MessagingPartySummary | null;
  className?: string;
}) {
  const id = coerceId(oid);
  if (id == null) return <span className="text-sm text-gray-400">—</span>;
  const name = partyDisplayName(summary, `Org ${id}`);
  return (
    <HoverShell
      className={className}
      card={<ProfileCardBody summary={summary} kind="org" id={id} />}
    >
      <Link
        to={`/admin/users/organization?oid=${id}`}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex max-w-full items-center gap-1.5 truncate text-sm font-medium text-blue-700 hover:underline"
      >
        <ProfileImage name={name} src={partyPhotoUrl(summary)} size="w-5 h-5" textSize="text-[9px]" />
        <span className="truncate">{name}</span>
      </Link>
    </HoverShell>
  );
}

export function MessageParties({
  msg,
  className,
}: {
  msg: AdminMessage;
  className?: string;
}) {
  if (isInquiryMessage(msg)) {
    const visitor = inquiryVisitorName(msg) || 'Visitor';
    return (
      <span className={cn('inline-flex max-w-full flex-wrap items-center gap-1 text-sm text-gray-700', className)}>
        <span className="inline-flex max-w-full items-center gap-1.5">
          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
            Visitor
          </span>
          <span className="truncate font-medium text-gray-900" title="Off-platform inquiry contact">
            {visitor}
          </span>
        </span>
        <span className="text-gray-400">→</span>
        {msg.recipient_uid != null && String(msg.recipient_uid) !== '' ? (
          <UserPartyLink uid={msg.recipient_uid} summary={msg.recipient_user_summary} />
        ) : msg.recipient_oid != null && String(msg.recipient_oid) !== '' ? (
          <OrgPartyLink oid={msg.recipient_oid} summary={msg.recipient_org_summary} />
        ) : (
          <span>lister</span>
        )}
      </span>
    );
  }

  const sender =
    msg.sender_kind === 'platform' || msg.is_platform ? (
      <span className="font-medium text-gray-800">FeelyFeely</span>
    ) : msg.sender_uid != null && String(msg.sender_uid) !== '' ? (
      <UserPartyLink uid={msg.sender_uid} summary={msg.sender_user_summary} />
    ) : msg.sender_oid != null && String(msg.sender_oid) !== '' ? (
      <OrgPartyLink oid={msg.sender_oid} summary={msg.sender_org_summary} />
    ) : (
      <span className="text-gray-400">—</span>
    );

  const recipient =
    msg.recipient_uid != null && String(msg.recipient_uid) !== '' ? (
      <UserPartyLink uid={msg.recipient_uid} summary={msg.recipient_user_summary} />
    ) : msg.recipient_oid != null && String(msg.recipient_oid) !== '' ? (
      <OrgPartyLink oid={msg.recipient_oid} summary={msg.recipient_org_summary} />
    ) : (
      <span className="text-gray-400">—</span>
    );

  return (
    <span className={cn('inline-flex max-w-full flex-wrap items-center gap-1', className)}>
      {sender}
      <span className="text-gray-400">→</span>
      {recipient}
    </span>
  );
}

export function ConversationParties({
  conversation,
  className,
}: {
  conversation: ConversationRegistry;
  className?: string;
}) {
  const key = conversation.conversation_key || '';
  const parts = key.split(':');
  const kind = parts[0];
  const users = conversation.user_summaries || {};
  const orgs = conversation.org_summaries || {};

  const user = (raw: string) => {
    const id = coerceId(raw);
    if (id == null) return <span>{raw}</span>;
    return <UserPartyLink uid={id} summary={users[String(id)]} />;
  };
  const org = (raw: string) => {
    const id = coerceId(raw);
    if (id == null) return <span>{raw}</span>;
    return <OrgPartyLink oid={id} summary={orgs[String(id)]} />;
  };

  let content: ReactNode = <span className="truncate text-sm text-gray-700">{key || 'Unknown thread'}</span>;
  if (kind === 'userpair' && parts.length >= 3) {
    content = (
      <>
        {user(parts[1])}
        <span className="text-gray-400">↔</span>
        {user(parts[2])}
      </>
    );
  } else if (kind === 'orguser' && parts.length >= 3) {
    content = (
      <>
        {org(parts[1])}
        <span className="text-gray-400">↔</span>
        {user(parts[2])}
      </>
    );
  } else if (kind === 'orgpair' && parts.length >= 3) {
    content = (
      <>
        {org(parts[1])}
        <span className="text-gray-400">↔</span>
        {org(parts[2])}
      </>
    );
  } else if (kind === 'platform' && parts[1] === 'user') {
    content = (
      <>
        <span className="font-medium text-gray-800">FeelyFeely</span>
        <span className="text-gray-400">→</span>
        {user(parts[2])}
      </>
    );
  } else if (kind === 'platform' && parts[1] === 'org') {
    content = (
      <>
        <span className="font-medium text-gray-800">FeelyFeely</span>
        <span className="text-gray-400">→</span>
        {org(parts[2])}
      </>
    );
  }

  return (
    <span className={cn('inline-flex max-w-full flex-wrap items-center gap-1', className)}>
      {content}
    </span>
  );
}

export function ThreadPartyLabel({
  party,
}: {
  party?: {
    uid?: number | null;
    oid?: number | null;
    user_summary?: MessagingPartySummary | null;
    org_summary?: MessagingPartySummary | null;
  } | null;
}) {
  if (!party) return <span className="text-gray-400">—</span>;
  if (party.uid != null) return <UserPartyLink uid={party.uid} summary={party.user_summary} />;
  if (party.oid != null) return <OrgPartyLink oid={party.oid} summary={party.org_summary} />;
  return <span className="text-gray-400">—</span>;
}
